// Værktøjslinjen: i tommelzonen på telefonen, som højre skinne i bredformat.
// Byg er standard. Opgradér og Station er tilstande; Montør og Fortryd er handlinger.

import type { Game } from '../game';
import type { ActionKind, Mechanic } from '../sim';
import { check, exposedLines, REINFORCE_ALL } from '../sim/actions';
import { CREW_COST, REINFORCE_COST, stationCost } from '../sim/grid';
import { h, kbd, setAttr, setText, toggle } from './dom';
import { ICON } from './icons';

export type Tool = 'build' | 'upgrade' | 'station';

export interface BarHandlers {
  tool(t: Tool): void;
  hire(): void;
  undo(): void;
  /** Forstærk alle udsatte master på én gang. */
  storm(): void;
}

interface ToolBtn {
  el: HTMLButtonElement;
  cost: HTMLElement;
  mech: Mechanic | null;
}

export class BottomBar {
  readonly el: HTMLElement;
  tool: Tool = 'build';
  private btns: Record<'build' | 'upgrade' | 'station' | 'hire' | 'storm' | 'undo', ToolBtn>;
  private crewBadge = h('span', { class: 'badge' });

  constructor(private handlers: BarHandlers) {
    const mk = (id: keyof BottomBar['btns'], icon: string, label: string, mech: Mechanic | null, onclick: () => void, primary = false): ToolBtn => {
      const cost = h('span', { class: 'tool-cost' });
      const el = h(
        'button',
        { type: 'button', class: `tool${primary ? ' primary' : ''}`, 'data-tool': id, onclick },
        h('span', { class: 'tool-icon', html: icon }),
        h('span', { class: 'tool-label' }, label),
        cost,
      );
      return { el, cost, mech };
    };
    this.btns = {
      build: mk('build', ICON.line, 'Byg', 'air', () => this.pick('build'), true),
      upgrade: mk('upgrade', ICON.upgrade, 'Opgradér', 'upgrade', () => this.pick('upgrade')),
      station: mk('station', ICON.station, 'Station', 'station', () => this.pick('station')),
      hire: mk('hire', ICON.crew, 'Montør', 'crew', () => this.handlers.hire()),
      storm: mk('storm', ICON.reinforce, 'Forstærk', 'reinforce', () => this.handlers.storm()),
      undo: mk('undo', ICON.undo, 'Fortryd', null, () => this.handlers.undo()),
    };
    this.btns.hire.el.querySelector('.tool-icon')?.append(this.crewBadge);
    kbd(this.btns.build.el, '1', '1', 'Byg ledninger (hold Shift for kabel)');
    kbd(this.btns.upgrade.el, '2', '2', 'Opgradér en ledning');
    kbd(this.btns.station.el, '3', '3', 'Byg en station');
    kbd(this.btns.hire.el, '4', '4', 'Hyr en montør');
    kbd(this.btns.storm.el, '5', '5', 'Forstærk alle udsatte master');
    this.btns.storm.el.classList.add('storm');
    kbd(this.btns.undo.el, 'Ctrl Z', 'Control+Z Meta+Z', 'Fortryd sidste ledning');
    setText(this.btns.hire.cost, `${CREW_COST} kr`);
    this.el = h('nav', { class: 'tools', id: 'tools', 'aria-label': 'Værktøjer' }, ...Object.values(this.btns).map((b) => b.el));
  }

  /** Et lille tryk på knappen, så en genvejstast kan ses på skærmen. */
  kick(id: keyof BottomBar['btns']): void {
    const el = this.btns[id].el;
    el.classList.remove('kick');
    void el.offsetWidth;
    el.classList.add('kick');
    el.addEventListener('animationend', () => el.classList.remove('kick'), { once: true });
  }

  /** Midten af stormknappen i vinduet, når den er synlig og kan trykkes. */
  stormCenter(): { x: number; y: number } | null {
    const b = this.btns.storm.el;
    if (b.hidden || b.disabled) return null;
    const r = b.getBoundingClientRect();
    return r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }

  pick(t: Tool): void {
    this.tool = this.tool === t && t !== 'build' ? 'build' : t;
    this.handlers.tool(this.tool);
  }

  update(game: Game, mechanics: readonly Mechanic[]): void {
    const s = game.state;
    // Makkerspillets roller (IGN-løkke runde 4): hver af jer har kun sine egne knapper.
    const theirs: Record<string, ActionKind> = { build: 'build_line', station: 'upgrade_station', undo: 'demolish', upgrade: 'upgrade_line', hire: 'hire_crew', storm: 'reinforce_mast' };
    const role = (id: string): boolean => {
      const k = theirs[id];
      return !k || !game.theirs(k);
    };
    for (const [id, b] of Object.entries(this.btns)) {
      const shown = (b.mech === null || mechanics.includes(b.mech)) && role(id);
      b.el.hidden = !shown;
      const pressed = id === this.tool;
      toggle(b.el, 'on', pressed);
      if (id === 'build' || id === 'upgrade' || id === 'station') setAttr(b.el, 'aria-pressed', String(pressed));
    }
    const canBuild = game.canBuildNow;
    const hire = check(s, 'hire_crew', undefined, undefined);
    this.btns.hire.el.disabled = !canBuild || !hire.ok;
    const vans = s.crews.filter((c) => c.kind === 'van');
    const idle = vans.filter((c) => c.task === 'idle').length;
    setText(this.crewBadge, `${idle}/${vans.length}`);
    setAttr(this.btns.hire.el, 'aria-label', `Hyr montør, ${CREW_COST} kroner. ${idle} af ${vans.length} hold er ledige.`);
    const sc = stationCost(s);
    setText(this.btns.station.cost, sc === 0 ? 'Gratis' : `${sc} kr`);
    this.btns.station.el.disabled = !canBuild || s.budget < sc;
    this.btns.upgrade.el.disabled = !canBuild;
    this.btns.build.el.disabled = !canBuild;
    // Stormknappen findes kun, mens en storm truer master, der ikke er forstærket.
    const exposed = mechanics.includes('reinforce') ? exposedLines(s).length : 0;
    this.btns.storm.el.hidden = exposed === 0 || !role('storm');
    // Stormknappen svæver over linjen på en smal telefon (style.css), så Fortryd bliver på sin plads.
    toggle(this.el, 'stormy', exposed > 0 && role('storm'));
    if (exposed > 0) {
      const cost = exposed * REINFORCE_COST;
      setText(this.btns.storm.cost, `${cost} kr`);
      setAttr(this.btns.storm.el, 'aria-label', `Forstærk ${exposed === 1 ? 'den udsatte mast' : `alle ${exposed} udsatte master`}, ${cost} kroner`);
      this.btns.storm.el.disabled = !check(s, 'reinforce_mast', REINFORCE_ALL, undefined).ok;
    }
    const undo = game.undoable;
    // Ikke disabled: et tryk skal kunne forklare, hvorfor der intet er at fortryde.
    setAttr(this.btns.undo.el, 'aria-disabled', String(!undo));
  }
}
